import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Mic, MicOff, Users, Clock, Radio, Square, LogOut, ArrowLeft, Trash2, Pencil,
  Loader2, CheckCircle2, AlertCircle, Copy, Video, QrCode, ExternalLink, Settings
} from 'lucide-react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Mic Level Visualizer ───
const MicLevel = ({ level, isRecording }) => {
  const bars = 12;
  return (
    <div className="flex items-end gap-0.5 h-8" data-testid="mic-level">
      {Array.from({ length: bars }).map((_, i) => {
        const threshold = (i / bars) * 100;
        const active = level > threshold && isRecording;
        return (
          <div key={i}
            className={cn(
              "w-1.5 rounded-full transition-all duration-75",
              active ? (i < bars * 0.5 ? "bg-emerald-400" : i < bars * 0.8 ? "bg-amber-400" : "bg-red-400") : "bg-slate-700"
            )}
            style={{ height: `${4 + (i * 2)}px` }}
          />
        );
      })}
    </div>
  );
};

// ─── Participant Row ───
const ParticipantRow = ({ p, isCurrentUser, isOnline }) => (
  <div className={cn("flex items-center gap-2.5 p-2.5 rounded-lg", isCurrentUser ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-slate-800/50")}>
    <div className="relative">
      <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold",
        p.role === 'host' ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-700 text-slate-300"
      )}>
        {p.name?.charAt(0)?.toUpperCase() || '?'}
      </div>
      <div className={cn("absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-slate-900", isOnline ? "bg-emerald-400" : "bg-slate-600")} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-white truncate">
        {p.name}{isCurrentUser ? ' (You)' : ''}
      </p>
      <p className="text-[10px] text-slate-500">{p.role === 'host' ? 'Host' : 'Participant'}{isOnline ? ' · Online' : ' · Offline'}</p>
    </div>
    <Mic className={cn("h-3.5 w-3.5", isOnline ? "text-emerald-400" : "text-slate-600")} />
  </div>
);

// ─── Main Room ───
export default function MeetingRoomPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id || user?._id || '';
  const userName = user?.full_name || user?.email || '';

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [micPermission, setMicPermission] = useState('pending');
  const [elapsed, setElapsed] = useState(0);
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [ending, setEnding] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [transcriptLines, setTranscriptLines] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [videoEnabled, setVideoEnabled] = useState(false);

  // Audio refs
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const chunkSeqRef = useRef(0);
  const animFrameRef = useRef(null);
  const chunkTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const recordingStartRef = useRef(null);
  const recognitionRef = useRef(null);
  const wsRef = useRef(null);
  const wsPingRef = useRef(null);

  const isHost = meeting?.host_id === userId;

  // ─── Fetch meeting data (poll every 3s) ───
  const fetchMeeting = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/meetings/${meetingId}`);
      setMeeting(res.data);

      // If meeting ended or completed, navigate
      if (res.data.status === 'processing') {
        navigate(`/meeting/${meetingId}/minutes`);
      }
      if (res.data.status === 'completed') {
        navigate(`/meeting/${meetingId}/minutes`);
      }
    } catch {
      toast.error('Failed to load meeting');
    }
    setLoading(false);
  }, [meetingId, navigate]);

  useEffect(() => {
    fetchMeeting();
    // Reduced polling (WS handles real-time events)
    const poll = setInterval(fetchMeeting, 10000);
    return () => clearInterval(poll);
  }, [fetchMeeting]);

  // ─── WebSocket real-time presence ───
  useEffect(() => {
    if (!meetingId || !user) return;
    const token = localStorage.getItem('konnect_access_token');
    if (!token) return;

    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    let ws;
    let reconnectTimer;

    const connect = () => {
      ws = new WebSocket(`${wsUrl}/ws/meeting/${meetingId}/${token}`);

      ws.onopen = () => {
        wsRef.current = ws;
        // Keepalive ping every 25s
        wsPingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'participant_joined' || msg.type === 'participant_left') {
            setOnlineUsers(msg.participants || []);
          }
          if (msg.type === 'meeting_status') {
            if (msg.status === 'processing' || msg.status === 'completed' || msg.status === 'error') {
              navigate(`/meeting/${meetingId}/minutes`);
            }
          }
        } catch { /* ignore non-JSON */ }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (wsPingRef.current) clearInterval(wsPingRef.current);
        // Reconnect after 3s
        reconnectTimer = setTimeout(connect, 3000);
      };

      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsPingRef.current) clearInterval(wsPingRef.current);
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [meetingId, user, navigate]);

  // Auto-start recording when meeting becomes active
  useEffect(() => {
    if (meeting?.status === 'active' && !isRecording && micPermission === 'granted') {
      startLocalRecording();
      startSpeechRecognition();
    }
    if (meeting?.status === 'processing' || meeting?.status === 'completed') {
      stopLocalRecording();
    }
  }, [meeting?.status, micPermission]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Request Microphone ───
  useEffect(() => {
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        setMicPermission('granted');

        // Setup audio analyser for level meter
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        const source = ctx.createMediaStreamSource(stream);
        source.connect(analyser);
        audioContextRef.current = ctx;
        analyserRef.current = analyser;

        // Animate mic level
        const updateLevel = () => {
          const data = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(data);
          const avg = data.reduce((a, b) => a + b, 0) / data.length;
          setMicLevel((avg / 255) * 100);
          animFrameRef.current = requestAnimationFrame(updateLevel);
        };
        updateLevel();
      } catch {
        setMicPermission('denied');
      }
    })();

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (audioContextRef.current) audioContextRef.current.close();
      stopLocalRecording();
    };
  }, []);

  // ─── Start Local Recording ───
  const startLocalRecording = useCallback(() => {
    if (!streamRef.current || isRecording) return;
    setIsRecording(true);
    chunkSeqRef.current = 0;
    recordingStartRef.current = Date.now();

    const startChunk = () => {
      if (!streamRef.current) return;
      try {
        const mr = new MediaRecorder(streamRef.current, { mimeType: 'audio/webm;codecs=opus' });
        mediaRecorderRef.current = mr;
        const chunks = [];
        const tsStart = Date.now();

        mr.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        mr.onstop = async () => {
          if (chunks.length === 0) return;
          const blob = new Blob(chunks, { type: 'audio/webm' });
          const tsEnd = Date.now();
          const seq = chunkSeqRef.current++;

          // Upload chunk
          const fd = new FormData();
          fd.append('meeting_id', meetingId);
          fd.append('user_id', userId);
          fd.append('user_name', userName);
          fd.append('chunk_seq', seq.toString());
          fd.append('timestamp_start', tsStart.toString());
          fd.append('timestamp_end', tsEnd.toString());
          fd.append('audio', blob, `chunk_${seq}.webm`);

          try {
            await axios.post(`${API}/api/meetings/audio-chunk`, fd);
          } catch {
            // Silent fail — don't interrupt recording
          }
        };

        mr.start();
        // Stop after 5 seconds, then start next chunk
        chunkTimerRef.current = setTimeout(() => {
          if (mr.state === 'recording') {
            mr.stop();
            startChunk(); // Start next chunk
          }
        }, 5000);
      } catch {
        // MediaRecorder not supported
      }
    };

    startChunk();

    // Elapsed time counter
    elapsedTimerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - recordingStartRef.current) / 1000));
    }, 1000);
  }, [meetingId, userId, userName, isRecording]);

  // ─── Stop Local Recording ───
  const stopLocalRecording = useCallback(() => {
    setIsRecording(false);
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (chunkTimerRef.current) clearTimeout(chunkTimerRef.current);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    stopSpeechRecognition();
  }, []);

  // ─── Browser Speech Recognition (Real-time Transcription) ───
  const startSpeechRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return; // Browser doesn't support

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          const text = result[0].transcript.trim();
          if (text) {
            setTranscriptLines(prev => [...prev.slice(-20), { speaker: userName, text }]);
            // Send transcript chunk to backend
            axios.post(`${API}/api/meetings/transcript-chunk`, {
              meeting_id: meetingId,
              user_id: userId,
              user_name: userName,
              text,
              is_final: true,
              timestamp: new Date().toISOString(),
            }).catch(() => {});
          }
        } else {
          interim += result[0].transcript;
        }
      }
      setLiveTranscript(interim);
    };

    recognition.onerror = (e) => {
      if (e.error !== 'no-speech' && e.error !== 'aborted') {
        console.warn('SpeechRecognition error:', e.error);
      }
    };

    recognition.onend = () => {
      // Auto-restart if still recording
      if (recognitionRef.current) {
        try { recognition.start(); } catch { /* already started */ }
      }
    };

    try { recognition.start(); } catch { /* */ }
  }, [meetingId, userId, userName]);

  const stopSpeechRecognition = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* */ }
      recognitionRef.current = null;
    }
    setLiveTranscript('');
  }, []);

  // ─── Host: Start Recording ───
  const handleStartRecording = async () => {
    try {
      await axios.post(`${API}/api/meetings/${meetingId}/start-recording`, {});
      toast.success('Recording started!');
      startLocalRecording();
      startSpeechRecognition();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start recording');
    }
  };

  // ─── Host: End Meeting ───
  const handleEndMeeting = async () => {
    setEnding(true);
    stopLocalRecording();
    try {
      await axios.post(`${API}/api/meetings/${meetingId}/end`, {});
      toast.success('Meeting ended. AI is generating minutes...');
      setEndConfirmOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to end meeting');
    }
    setEnding(false);
  };

  // ─── Leave Meeting ───
  const handleLeave = () => {
    stopLocalRecording();
    navigate('/meeting');
  };

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  if (loading) {
    return (
      <div className="min-h-[80vh] -m-4 md:-m-6 lg:-m-8 flex items-center justify-center bg-slate-900">
        <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-[80vh] -m-4 md:-m-6 lg:-m-8 flex items-center justify-center bg-slate-900">
        <p className="text-slate-400">Meeting not found</p>
      </div>
    );
  }

  // ─── Processing Screen ───
  if (meeting.status === 'processing') {
    return (
      <div className="min-h-[80vh] -m-4 md:-m-6 lg:-m-8 flex items-center justify-center bg-slate-900" data-testid="meeting-processing">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
            <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
          </div>
          <h2 className="text-lg font-bold text-white mb-1">Generating Meeting Minutes</h2>
          <p className="text-sm text-slate-400">AI is transcribing and analyzing your meeting. This takes 1-3 minutes.</p>
          <p className="text-xs text-slate-500 mt-4">{meeting.chunk_count} audio chunks recorded · {meeting.participants?.length} participants</p>
        </div>
      </div>
    );
  }

  const participants = meeting.participants || [];
  const isPresentationMode = meeting.mode === 'presentation';
  const canStartRecording = isHost && (isPresentationMode ? true : participants.length >= 2) && meeting.status === 'waiting';
  const publicUrl = isPresentationMode && meeting.public_token ? `${window.location.origin}/join/${meeting.public_token}` : null;

  return (
    <div className="min-h-[80vh] -m-4 md:-m-6 lg:-m-8 p-4 md:p-6 bg-slate-900" data-testid="meeting-room">
      <div className="max-w-2xl mx-auto">
        {/* Navigation Bar */}
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => navigate('/meeting')} className="text-slate-400 hover:text-white gap-1.5 h-8 px-2" data-testid="back-to-meetings">
            <ArrowLeft className="h-4 w-4" /> Back to Meetings
          </Button>
          <div className="flex items-center gap-1">
            {isHost && meeting.status === 'waiting' && (
              <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 gap-1 text-xs"
                onClick={async () => {
                  if (!window.confirm('Delete this meeting permanently?')) return;
                  try {
                    await axios.delete(`${API}/api/meetings/${meetingId}`);
                    toast.success('Meeting deleted');
                    navigate('/meeting');
                  } catch { toast.error('Failed to delete'); }
                }} data-testid="delete-meeting-btn">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </Button>
            )}
          </div>
        </div>

        {/* Meeting Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            {meeting.status === 'active' && <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />}
            <h1 className="text-xl font-bold text-white">{meeting.title}<PageInfoTooltip pageKey="meeting-room" /></h1>
          </div>
          <div className="flex items-center justify-center gap-3 text-xs text-slate-400">
            <span className="flex items-center gap-1"><Users className="h-3 w-3" />{participants.length} joined</span>
            {meeting.status === 'active' && <span className="flex items-center gap-1 text-red-400"><Radio className="h-3 w-3" />REC {formatTime(elapsed)}</span>}
            <button onClick={() => { navigator.clipboard.writeText(meeting.code); toast.success('Code copied!'); }}
              className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300" data-testid="copy-code-btn">
              <Copy className="h-3 w-3" />Code: {meeting.code}
            </button>
          </div>
        </div>

        {/* Agenda */}
        <Card className="bg-slate-800/60 border-slate-700/50 mb-4">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-medium">Agenda</p>
            <div className="space-y-1">
              {meeting.agenda?.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-slate-300">
                  <span className="w-4 h-4 rounded bg-slate-700 flex items-center justify-center shrink-0 text-[10px] text-slate-500">{i + 1}</span>
                  {item}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Video Conferencing (Jitsi Meet) */}
        <Card className="bg-slate-800/60 border-slate-700/50 mb-4">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5">
                <Video className="h-3 w-3" /> Video Conference
              </p>
              <Button size="sm" variant={videoEnabled ? "destructive" : "default"}
                className={cn("h-7 text-[10px] gap-1", !videoEnabled && "bg-emerald-600 hover:bg-emerald-700")}
                onClick={() => setVideoEnabled(!videoEnabled)} data-testid="toggle-video-btn">
                <Video className="h-3 w-3" /> {videoEnabled ? 'End Video' : 'Start Video'}
              </Button>
            </div>
            {videoEnabled && (
              <div className="relative rounded-lg overflow-hidden bg-black" style={{ height: 400 }}>
                <iframe
                  src={`https://meet.jit.si/konnect-${meeting.code}#config.prejoinConfig.enabled=false&config.prejoinPageEnabled=false&config.startWithAudioMuted=false&config.startWithVideoMuted=false&config.disableDeepLinking=true&config.hideConferenceSubject=true&config.hideConferenceTimer=false&interfaceConfig.SHOW_BRAND_WATERMARK=false&interfaceConfig.SHOW_JITSI_WATERMARK=false&interfaceConfig.SHOW_WATERMARK_FOR_GUESTS=false&interfaceConfig.SHOW_POWERED_BY=false&interfaceConfig.SHOW_PROMOTIONAL_CLOSE_PAGE=false&interfaceConfig.DEFAULT_LOGO_URL=&interfaceConfig.DEFAULT_WELCOME_PAGE_LOGO_URL=&interfaceConfig.JITSI_WATERMARK_LINK=&interfaceConfig.APP_NAME=KonnectMeet&interfaceConfig.NATIVE_APP_NAME=KonnectMeet&interfaceConfig.PROVIDER_NAME=Konnect&userInfo.displayName=${encodeURIComponent(userName)}`}
                  allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write"
                  className="w-full h-full border-0"
                  title="KonnectMeet Video"
                  data-testid="jitsi-frame"
                />
                {/* Konnect Logo Overlay — covers top-left Jitsi branding */}
                <div className="absolute top-0 left-0 bg-[#1a1a2e] px-3 py-1.5 pointer-events-none z-10 rounded-br-lg">
                  <span className="text-xs font-bold text-emerald-400 tracking-wider">KonnectMeet</span>
                </div>
                {/* Cover top-right Jitsi watermark */}
                <div className="absolute top-0 right-0 bg-[#1a1a2e] px-3 py-1.5 pointer-events-none z-10 rounded-bl-lg">
                  <span className="text-[10px] font-medium text-emerald-400/70">Konnect</span>
                </div>
              </div>
            )}
            {!videoEnabled && (
              <p className="text-[10px] text-slate-500 text-center py-3">Click "Start Video" to launch video conferencing with screen share and recording</p>
            )}
          </CardContent>
        </Card>

        {/* Presentation Mode: Share Link & QR */}
        {isPresentationMode && publicUrl && meeting.status === 'waiting' && (
          <Card className="bg-emerald-500/5 border-emerald-500/20 mb-4" data-testid="share-client-card">
            <CardContent className="p-4">
              <p className="text-xs font-semibold text-emerald-400 mb-3 flex items-center gap-1.5">
                <QrCode className="h-3.5 w-3.5" />Share with Client
              </p>
              <div className="flex gap-4">
                <div className="bg-white rounded-lg p-2 shrink-0">
                  <QRCodeSVG value={publicUrl} size={100} level="M" />
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-1.5 p-2 bg-slate-900/50 rounded border border-slate-700/30">
                    <input className="flex-1 text-[11px] text-slate-300 bg-transparent outline-none font-mono" value={publicUrl} readOnly data-testid="room-presentation-url" />
                    <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Link copied!'); }}
                      className="text-emerald-400 hover:text-emerald-300 shrink-0" data-testid="room-copy-url">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-500">Client can scan the QR code or click the link to join from any device. No login needed.</p>
                  <Button size="sm" variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-[11px] h-7 gap-1.5"
                    onClick={() => window.open(publicUrl, '_blank')} data-testid="room-open-client-link">
                    <ExternalLink className="h-3 w-3" />Preview Client View
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mic Permission Error */}
        {micPermission === 'denied' && (
          <Card className="bg-red-500/10 border-red-500/30 mb-4">
            <CardContent className="p-3 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
              <p className="text-xs text-red-300">Microphone access denied. Please allow microphone permission in your browser settings to participate in the meeting.</p>
            </CardContent>
          </Card>
        )}

        {/* My Mic Level */}
        {micPermission === 'granted' && (
          <Card className="bg-slate-800/60 border-slate-700/50 mb-4">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", isRecording ? "bg-red-500/20" : "bg-slate-700")}>
                    {isRecording ? <Mic className="h-4 w-4 text-red-400" /> : <MicOff className="h-4 w-4 text-slate-500" />}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-white">Your Microphone</p>
                    <p className="text-[10px] text-slate-500">{isRecording ? 'Recording & transcribing' : meeting.status === 'waiting' ? 'Waiting for host to start' : 'Ready'}</p>
                  </div>
                </div>
                <MicLevel level={micLevel} isRecording={micPermission === 'granted'} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Live Transcript */}
        {isRecording && (transcriptLines.length > 0 || liveTranscript) && (
          <Card className="bg-slate-800/60 border-slate-700/50 mb-4" data-testid="live-transcript">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-medium flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Live Transcript
              </p>
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {transcriptLines.slice(-8).map((line, i) => (
                  <p key={i} className="text-xs text-slate-300">
                    <span className="text-emerald-400 font-medium">{line.speaker}: </span>{line.text}
                  </p>
                ))}
                {liveTranscript && (
                  <p className="text-xs text-slate-500 italic">{liveTranscript}...</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Participants */}
        <Card className="bg-slate-800/60 border-slate-700/50 mb-4">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-medium">
              Participants ({participants.length})
              {onlineUsers.length > 0 && <span className="ml-1.5 inline-flex items-center gap-1 text-emerald-400"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />{onlineUsers.length} live</span>}
            </p>
            <div className="space-y-1.5">
              {participants.map((p, i) => (
                <ParticipantRow key={i} p={p} isCurrentUser={p.user_id === userId} isOnline={onlineUsers.some(ou => ou.user_id === p.user_id)} />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-2 justify-center">
          {canStartRecording && (
            <Button className="bg-red-600 hover:bg-red-500 gap-2 px-6" onClick={handleStartRecording} data-testid="start-recording-btn">
              <Radio className="h-4 w-4" />Start Recording
            </Button>
          )}
          {isHost && meeting.status === 'active' && (
            <Button className="bg-red-600 hover:bg-red-500 gap-2 px-6" onClick={() => setEndConfirmOpen(true)} data-testid="end-meeting-btn">
              <Square className="h-4 w-4" />End Meeting
            </Button>
          )}
          {!isHost && (
            <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 gap-2" onClick={handleLeave} data-testid="leave-meeting-btn">
              <LogOut className="h-4 w-4" />Leave Meeting
            </Button>
          )}
        </div>

        {/* Waiting message for host */}
        {isHost && meeting.status === 'waiting' && participants.length < 2 && (
          <p className="text-center text-xs text-amber-400/70 mt-4">Waiting for at least 1 more participant to join before recording can start.</p>
        )}
      </div>

      {/* End Meeting Confirm Dialog */}
      <Dialog open={endConfirmOpen} onOpenChange={setEndConfirmOpen}>
        <DialogContent className="max-w-sm bg-slate-800 border-slate-700 text-white" aria-describedby="end-meeting-desc">
          <DialogHeader><DialogTitle className="text-white">End Meeting?</DialogTitle></DialogHeader>
          <p id="end-meeting-desc" className="text-sm text-slate-400">This will stop recording and trigger AI minutes generation. All participants will be notified when minutes are ready.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEndConfirmOpen(false)} className="text-slate-400">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-500" onClick={handleEndMeeting} disabled={ending} data-testid="confirm-end-meeting">
              {ending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Square className="h-4 w-4 mr-2" />}
              End Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
